/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as $ from "jquery";
import {UiField} from "./UiField";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {UiCompositeFieldConfig} from "../../generated/UiCompositeFieldConfig";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {
	UiRichTextEditor_ImageUploadFailedSubEvent,
	UiRichTextEditor_ImageUploadStartedSubEvent,
	UiRichTextEditor_ImageUploadSuccessfulSubEvent,
	UiRichTextEditor_ImageUploadTooLargeSubEvent,
	UiRichTextEditorConfig,
	UiRichTextEditorSubCommandHandler,
	UiRichTextEditorSubEventSource
} from "../../generated/UiRichTextEditorConfig";
import * as tinymce from 'tinymce';
import {Editor, Settings} from 'tinymce';
import 'tinymce/themes/modern/theme';
import {executeWhenAttached} from "../util/ExecuteWhenAttached";
import {DeferredExecutor} from "../util/DeferredExecutor";
import {generateUUID, keyCodes} from "trivial-components";
// Any plugins you want to use has to be imported
import 'tinymce/plugins/lists';
import 'tinymce/plugins/table';
import 'tinymce/plugins/image';
import 'tinymce/plugins/imagetools';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/contextmenu';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/spellchecker';
import 'tinymce/plugins/textcolor';
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {SubEventFactory} from "../../generated/SubEventFactory";
import {debouncedMethod, DebounceMode} from "../util/debounce";
import {UiToolbarVisibilityMode} from "../../generated/UiToolbarVisibilityMode";
import {UiSpinner} from "../micro-components/UiSpinner";
import {UiTextInputHandlingField_SpecialKeyPressedSubEvent, UiTextInputHandlingField_TextInputSubEvent} from "../../generated/UiTextInputHandlingFieldConfig";
import {UiSpecialKey} from "../../generated/UiSpecialKey";


/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiRichTextEditor extends UiField<UiRichTextEditorConfig, string> implements UiRichTextEditorSubEventSource, UiRichTextEditorSubCommandHandler {

	public readonly onTextInput: TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent>(this, 10000);
	public readonly onSpecialKeyPressed: TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent>(this, 250);
	public readonly onImageUploadFailed: TeamAppsEvent<UiRichTextEditor_ImageUploadFailedSubEvent> = new TeamAppsEvent<UiRichTextEditor_ImageUploadFailedSubEvent>(this);
	public readonly onImageUploadStarted: TeamAppsEvent<UiRichTextEditor_ImageUploadStartedSubEvent> = new TeamAppsEvent<UiRichTextEditor_ImageUploadStartedSubEvent>(this);
	public readonly onImageUploadSuccessful: TeamAppsEvent<UiRichTextEditor_ImageUploadSuccessfulSubEvent> = new TeamAppsEvent<UiRichTextEditor_ImageUploadSuccessfulSubEvent>(this);
	public readonly onImageUploadTooLarge: TeamAppsEvent<UiRichTextEditor_ImageUploadTooLargeSubEvent> = new TeamAppsEvent<UiRichTextEditor_ImageUploadTooLargeSubEvent>(this);

	private static readonly TRANSLATION_FILES: { [languageIso: string]: string } = {
		"af": "af_ZA.js",
		"ar": "ar.js",
		"be": "be.js",
		"bg": "bg_BG.js",
		"ca": "ca.js",
		"cs": "cs.js",
		"cy": "cy.js",
		"da": "da.js",
		"de": "de.js",
		"dv": "dv.js",
		"el": "el.js",
		"es": "es.js",
		"et": "et.js",
		"fr": "fr_FR.js",
		"ga": "ga.js",
		"gl": "gl.js",
		"he": "he_IL.js",
		"hr": "hr.js",
		"hu": "hu_HU.js",
		"it": "it.js",
		"ja": "ja.js",
		"ka": "ka_GE.js",
		"kab": "kab.js",
		"kk": "kk.js",
		"km": "km_KH.js",
		"ko": "ko_KR.js",
		"lv": "lv.js",
		"nb": "nb_NO.js",
		"nl": "nl.js",
		"pl": "pl.js",
		"pt": "pt_BR.js",
		"ro": "ro.js",
		"ru": "ru.js",
		"sk": "sk.js",
		"sl": "sl_SI.js",
		"sv": "sv_SE.js",
		"ta": "ta.js",
		"th": "th_TH.js",
		"tr": "tr.js",
		"uk": "uk.js",
		"uz": "uz.js",
		"vi": "vi_VN.js",
		"zh": "zh_CN.js"
	};


	private $main: JQuery;
	private $toolbarContainer: JQuery;
	private editor: Editor;
	private mceReadyExecutor: DeferredExecutor;
	private uuid: string;
	private imageUploadSuccessCallbacksByUuid: { [fileUuid: string]: (location: string) => void } = {};
	private $fileField: JQuery;
	private buttonGroupWidths: number[];
	private _hasFocus: boolean;
	private toolbarVisibilityMode: UiToolbarVisibilityMode;
	private maxImageFileSizeInBytes: number;
	private uploadUrl: string;
	private runningImageUploadsCount: number = 0;
	private $spinnerWrapper: JQuery;
	private destroying: boolean;
	private $readonlyView: JQuery;
	private initializationStarted: boolean;

	protected initialize(config: UiRichTextEditorConfig, targetElement: Element, context: TeamAppsUiContext) {
		this.toolbarVisibilityMode = config.toolbarVisibilityMode;
		this.setMaxImageFileSizeInBytes(config.maxImageFileSizeInBytes);
		this.setUploadUrl(config.uploadUrl);

		this.mceReadyExecutor = new DeferredExecutor();
		this.uuid = "c-" + generateUUID();
		this.$main = $(`<div class="UiRichTextEditor teamapps-input-wrapper" id="${this.uuid}">
			<div class="toolbar-container"></div>
			<div class="inline-editor"></div>
			<input type="file" class="file-upload-button">
			<div class="spinner-wrapper hidden"></div>
			<div class="readonly-view"></div>
		</div>`)
			.appendTo(targetElement);
		this.$toolbarContainer = this.$main.find('.toolbar-container');
		this.$spinnerWrapper = this.$main.find('.spinner-wrapper');
		this.$readonlyView = this.$main.find('.readonly-view');
		new UiSpinner().getMainDomElement().appendTo(this.$spinnerWrapper);
		this.$fileField = this.$main.find('.file-upload-button');
		this.$fileField.on("change", (e) => {
			let files = (<HTMLInputElement> this.$fileField[0]).files;
			for (let i = 0; i < files.length; i++) {
				let file = files.item(i);
				if (file.size > this.maxImageFileSizeInBytes) {
					this.onImageUploadTooLarge.fire(SubEventFactory.createUiRichTextEditor_ImageUploadTooLargeSubEvent(file.name, file.type, file.size));
				} else {
					let fileReader = new FileReader();
					fileReader.onload = (e) => {
						let dataUrl = (e.target as any).result;
						this.editor.insertContent(`<img src="${dataUrl}" width="300">`);
						this.editor.uploadImages(() => undefined);
					};
					fileReader.readAsDataURL(files.item(0));
					this.$fileField.val('');
				}
			}
		});
		this.initTinyMce();

		this.setMinHeight(config.minHeight);
		this.setMaxHeight(config.maxHeight);
	}

	@executeWhenAttached()
	private initTinyMce() {
		if (this.isEditable() && !this.initializationStarted) {
			this.initializationStarted = true;
			tinymce.init({
				theme_url: '/static/runtime-resources/tinymce/themes/modern/theme.js',
				skin_url: '/static/runtime-resources/tinymce/skins/lightgray/',
				selector: `#${this.uuid} > .inline-editor`,
				fixed_toolbar_container: `#${this.uuid} > .toolbar-container`,
				branding: false,
				menubar: false,
				inline: true,
				toolbar: "undo redo | styleselect | bold italic underline forecolor backcolor removeformat | alignleft aligncenter alignright alignjustify | blockquote bullist numlist outdent indent | insertimage table | overflowbutton",
				plugins: 'lists table image imagetools link autolink contextmenu searchreplace textcolor',
				contextmenu: "openlink link unlink searchreplace",
				language_url: "/static/runtime-resources/tinymce/langs/" + UiRichTextEditor.TRANSLATION_FILES[this._context.config.isoLanguage],
				entity_encoding: "raw",
				custom_elements: 'alert',
				style_formats: [{
					title: 'Heading 1',
					format: 'h1'
				}, {
					title: 'Heading 2',
					format: 'h2'
				}, {
					title: 'Paragraph',
					format: 'p'
				}, {
					title: 'Blockquote',
					format: 'blockquote'
				}, {
					title: 'Note',
					block: 'alert',
					wrapper: 1,
					remove: 'all'
				}, {
					title: 'Pre',
					format: 'pre',
				}, {
					title: 'Code',
					icon: 'code',
					format: 'code'
				}, {
					title: '|'
				}, {
					title: 'Headings',
					items: [{
						title: 'Heading 1',
						format: 'h1'
					}, {
						title: 'Heading 2',
						format: 'h2'
					}, {
						title: 'Heading 3',
						format: 'h3'
					}, {
						title: 'Heading 4',
						format: 'h4'
					}, {
						title: 'Heading 5',
						format: 'h5'
					}, {
						title: 'Heading 6',
						format: 'h6'
					}]
				}, {
					title: 'Blocks',
					items: [{
						title: 'Paragraph',
						format: 'p'
					}, {
						title: 'Blockquote',
						format: 'blockquote'
					}, {
						title: 'Note',
						block: 'alert',
						wrapper: 1,
						remove: 'all'
					}, {
						title: 'Pre',
						format: 'pre'
					}]
				}, {
					title: 'Inline',
					items: [{
						title: 'Bold',
						icon: 'bold',
						format: 'bold'
					}, {
						title: 'Italic',
						icon: 'italic',
						format: 'italic'
					}, {
						title: 'Underline',
						icon: 'underline',
						format: 'underline'
					}, {
						title: 'Strikethrough',
						icon: 'strikethrough',
						format: 'strikethrough'
					}, {
						title: 'Superscript',
						icon: 'superscript',
						format: 'superscript'
					}, {
						title: 'Subscript',
						icon: 'subscript',
						format: 'subscript'
					}, {
						title: 'Code',
						icon: 'code',
						format: 'code'
					}]
				}],
				init_instance_callback: editor => {
					if (this.destroying) { // already destroyed
						editor.destroy();
						return;
					}
					this.editor = editor;

					editor.addMenuItem('bullist', {text: 'Bullet list', icon: 'bullist', cmd: 'InsertUnorderedList'});
					editor.addMenuItem('numlist', {text: 'Numbered list', icon: 'numlist', cmd: 'InsertOrderedList'});
					editor.addMenuItem('insertimage', {icon: 'image', text: 'Insert image', onclick: () => this.$fileField[0].click()});
					editor.addButton('overflowbutton', {
						type: 'MenuButton',
						image: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAACXBIWXMAAAsTAAALEwEAmpwYAAABeUlEQVRYhe2TsWoUQRzGf//dWbYysZW7vIDBOq1NiK2+QcRU6RI4mNtbWLi7DRzoC0iSXkFfQZLq7NRHiBZ2Aatjb/ZvMx66mW1tMj9YmP3mm28+ZhiIRCKRyH1H/gzKsnzinHsN7KnqjYi8qev6IrRoNBo9MMacAS+89GG9XtvFYvEr5B+Pxy9V9UREdoBlmqan0+n0G0DiA4fOuWtgH9gSkV3g3Fp7GAo0xrwHjoFH/jvOsuxd3+bAuc/cAvadc1dFUQw2BYwxr4DtwPrTrlCW5S5w0NVV9Zm19nFAPwnkPlTVo00BYCfUXkSGXa1pmkHI6/135vyx92Yn/mfZk/m5KyRJ8gVoAt4G+BrQg9lt2y43BbIsuwCuO57bNE3vXEFd1z8B25EVsH7uH3zGbUe+yvP8Ev56BVVVmdVqdZgkyZ6qfheRt/P5/EeoPcBkMnnqnHvuN/k4m80+9XmLohio6pGIDNu2XeZ5fllV1brPH4lEIpHIf+U3FW15e2UaPR8AAAAASUVORK5CYII=',
						title: '...',
						menu: [
							(editor as any).menuItems['undo'],
							(editor as any).menuItems['redo'],
							(editor as any).menuItems['formats'],
							(editor as any).menuItems['bold'],
							(editor as any).menuItems['italic'],
							(editor as any).menuItems['removeformat'],
							(editor as any).menuItems['align'],
							(editor as any).menuItems['bullist'],
							(editor as any).menuItems['numlist'],
							(editor as any).menuItems['insertimage'],
							(editor as any).menuItems['inserttable'],
							(editor as any).menuItems['openlink'],
							(editor as any).menuItems['link'],
							(editor as any).menuItems['unlink'],
							(editor as any).menuItems['searchreplace']
						]
					});
					editor.addButton('insertimage', {icon: 'image', tooltip: 'Insert image', onclick: () => this.$fileField[0].click()});
					this.editor.fire("focus"); // triggers the initial rendering of the toolbar
					this.editor.fire("blur"); // make the editor NOT being rendered as focused
					this.updateToolbarOverflow(); // the toolbar got visible only now...

					this.mceReadyExecutor.ready = true;

					this.onResize();
				},
				setup: (editor) => {
					editor.on('change undo redo keypress', (e) => {
						this.fireTextInputEvent();
					});
					editor.on('keydown', (e) => {
						if (e.keyCode === keyCodes.escape) {
							this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ESCAPE));
						} else if (e.keyCode === keyCodes.enter) {
							this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ENTER));
						}
					});
					editor.on('undo redo', (e) => {
						this.editor.uploadImages(() => undefined);
					});
					editor.on('focus', (e) => {
						this._hasFocus = true;
						this.getMainDomElement().addClass('focus');
						this.updateToolbarVisibility();
					});
					editor.on('blur', (e) => {
						this._hasFocus = false;
						this.getMainDomElement().removeClass('focus');
						if (this.mayFireChangeEvents()) {
							this.commit();
						}
						this.updateToolbarVisibility();
					});
				},
				images_upload_handler: (blobInfo: { base64: () => string, blob: () => Blob, blobUri: () => string, filename: () => string, id: () => string, name: () => string, uri: () => string }, success, failure) => {
					this.runningImageUploadsCount++;
					this.updateSpinnerVisibility();
					this.onImageUploadStarted.fire(SubEventFactory.createUiRichTextEditor_ImageUploadStartedSubEvent(blobInfo.filename(), blobInfo.blob().type, blobInfo.blob().size, this.runningImageUploadsCount));

					let upload = (isFirstUpload: boolean) => {
						let xhr: XMLHttpRequest;
						let formData: FormData;

						xhr = new XMLHttpRequest();
						xhr.withCredentials = false;
						xhr.open('POST', this.uploadUrl);

						const handleUploadFailure = () => {
							if (isFirstUpload) {
								setTimeout(() => upload(false), 5000);
							} else {
								this.runningImageUploadsCount--;
								this.updateSpinnerVisibility();
								this.onImageUploadFailed.fire(SubEventFactory.createUiRichTextEditor_ImageUploadFailedSubEvent(blobInfo.filename(), blobInfo.blob().type, blobInfo.blob().size, this.runningImageUploadsCount));
								this.editor.undoManager.transact(() => {
									this.$main.find(`[src='${blobInfo.blobUri()}']`).remove();
								});
								failure(null);
							}
						};

						xhr.onload = () => {
							if (xhr.status < 200 || xhr.status >= 300) {
								handleUploadFailure();
							} else {
								let fileUuid = JSON.parse(xhr.responseText)[0];
								this.imageUploadSuccessCallbacksByUuid[fileUuid] = success;
								this.runningImageUploadsCount--;
								this.updateSpinnerVisibility();
								this.onImageUploadSuccessful.fire(SubEventFactory.createUiRichTextEditor_ImageUploadSuccessfulSubEvent(fileUuid, blobInfo.filename(), blobInfo.blob().type, blobInfo.blob().size, this.runningImageUploadsCount));
							}
						};
						xhr.onerror = () => {
							handleUploadFailure();
						};

						formData = new FormData();
						formData.append('files', blobInfo.blob(), blobInfo.filename());

						xhr.send(formData);
					};
					upload(true);
				}
			} as Settings);
		}
	}

	isValidData(v: string): boolean {
		return v == null || typeof v === "string";
	}

	private fireTextInputEvent() {
		if (this.mayFireChangeEvents()) {
			this.onTextInput.fire(SubEventFactory.createUiTextInputHandlingField_TextInputSubEvent(this.getTransientValue()));
		}
	}

	private updateSpinnerVisibility() {
		this.$spinnerWrapper.toggleClass("hidden", this.runningImageUploadsCount === 0);
	}

	setUploadedImageUrl(fileUuid: string, url: string): void {
		let successCallback = this.imageUploadSuccessCallbacksByUuid[fileUuid];
		if (successCallback != null) {
			successCallback(url);
			this.fireTextInputEvent(); // the html has changed at this point with the replacement of the data URL with the server-side URL
		}
	}

	public getMainDomElement(): JQuery {
		return this.$main;
	}

	public getFocusableElement(): JQuery {
		return this.editor != null ? $(this.editor.getBody()) : null;
	}

	protected displayCommittedValue(): void {
		this.mceReadyExecutor.invokeWhenReady(() => {
			this.editor.setContent(this.getCommittedValue() != null ? this.getCommittedValue() || '' : '');
			this.editor.undoManager.clear();
		});
		if (!this.isEditable()) {
			this.$readonlyView.html(this.getCommittedValue());
		}
	}

	focus(): void {
		this.editor && this.editor.focus(false);
	}

	destroy(): void {
		this.destroying = true;
		if (this.editor != null) {
            this.editor.destroy();
        }
	}

	getTransientValue(): string {
		let content = this.editor != null ? this.editor.getContent() : this.getCommittedValue();
		return content.replace(/src="data:.*?"/g, "");
	}

	@debouncedMethod(700, DebounceMode.BOTH)
	@executeWhenAttached(true)
	onResize(): void {
		this.updateToolbarOverflow();
	}

	private updateToolbarOverflow() {
		if (!this.buttonGroupWidths) {
			let buttonGroupWidths = this.$toolbarContainer.find(".mce-btn-group:not(:last-child)").toArray()
				.map(e => e.offsetWidth);
			if (buttonGroupWidths.reduce((sum, groupWidth) => sum + groupWidth, 0) === 0) { // toolbar apparently not visible
				return;
			} else {
				this.buttonGroupWidths = buttonGroupWidths;
			}
		}

		let availableWidth = this.$toolbarContainer[0].offsetWidth - 50 /*overflowbutton*/;
		let $groupButtons = this.$toolbarContainer.find(".mce-btn-group:not(:last-child)");
		let consumedWidth = 0;
		this.buttonGroupWidths.forEach((groupWidth, index) => {
			consumedWidth = consumedWidth + groupWidth;
			$($groupButtons.get(index)).toggleClass('hidden', consumedWidth > availableWidth);
		});
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode, oldEditingMode: UiFieldEditingMode): void {
		let wasEditable = !(oldEditingMode === UiFieldEditingMode.DISABLED || oldEditingMode === UiFieldEditingMode.READONLY);
		if (this.isEditable()) {
			this.initTinyMce();
		} else if (wasEditable && !this.isEditable()) {
			this.$readonlyView.html(this.getCommittedValue());
		}
		UiField.defaultOnEditingModeChangedImpl(this);
		this.$main.toggleClass("editable-if-focused", editingMode === UiFieldEditingMode.EDITABLE_IF_FOCUSED);
		this.updateToolbarVisibility();
	}

	getDefaultValue(): string {
		return "";
	}

	setToolbarVisibilityMode(toolbarVisibilityMode: UiToolbarVisibilityMode): void {
		this.toolbarVisibilityMode = toolbarVisibilityMode;
		this.updateToolbarVisibility();
	}

	private updateToolbarVisibility() {
		const toolbarVisible =
			(this.isEditable() && (this.toolbarVisibilityMode === UiToolbarVisibilityMode.VISIBLE || (this.toolbarVisibilityMode === UiToolbarVisibilityMode.VISIBLE_IF_FOCUSED && this._hasFocus)));
		this.$toolbarContainer.toggleClass('hidden', !toolbarVisible);
		this.updateToolbarOverflow();
	}

	hasFocus(): boolean {
		return this._hasFocus;
	}

	setMaxImageFileSizeInBytes(maxImageFileSizeInBytes: number): void {
		this.maxImageFileSizeInBytes = maxImageFileSizeInBytes;
	}

	setUploadUrl(uploadUrl: string): void {
		this.uploadUrl = uploadUrl;
	}

	public static getReadOnlyHtml(config: UiCompositeFieldConfig, value: string, context: TeamAppsUiContext, drawBorder: boolean): string {
		return `<div class="static-readonly-UiRichTextEditor ${drawBorder ? "bordered" : ""}">${value || ""}</div>`;
	}

	public valuesChanged(v1: string, v2: string): boolean {
		return v1 !== v2;
	}

	private mayFireChangeEvents() {
		return this.mceReadyExecutor.ready && !this.destroying;
	}

	public setMinHeight(minHeight: number) {
		this.$main.css("min-height", minHeight ? minHeight + "px" : "");
	}

	public setMaxHeight(maxHeight: number) {
		this.$main.css("max-height", maxHeight ? maxHeight + "px" : "");
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiRichTextEditor", UiRichTextEditor);